import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, gbp } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

function mondayOf(d) {
  const date = new Date(d + "T00:00:00");
  const day = date.getDay(); // 0=Sun..6=Sat
  const diff = day === 0 ? -6 : 1 - day; // shift to Monday
  date.setDate(date.getDate() + diff);
  const tz = date.getTimezoneOffset() * 60000;
  return new Date(date - tz).toISOString().slice(0, 10);
}

function todayISO() {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 10);
}

export default function ManagerFoodCost() {
  const [weekStart, setWeekStart] = useState(mondayOf(todayISO()));
  const [amount, setAmount] = useState("");
  const [existingId, setExistingId] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/food-costs");
      setHistory(data.food_costs);
      const match = data.food_costs.find((r) => r.week_start === weekStart);
      if (match) { setExistingId(match.id); setAmount(String(match.amount)); }
      else { setExistingId(null); setAmount(""); }
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/food-costs", { week_start: weekStart, amount: parseFloat(amount || "0") });
      toast.success(existingId ? "Food cost updated" : "Food cost saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this weekly food cost?")) return;
    try {
      await api.delete(`/food-costs/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const onWeekChange = (raw) => {
    if (!raw) return;
    setWeekStart(mondayOf(raw));
  };

  return (
    <div className="p-6 md:p-8 max-w-[1100px]">
      <div className="mb-6">
        <div className="label-uc">Weekly Food Cost</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Cost of goods</h1>
        <p className="text-sm text-zinc-500 mt-1">Log the total food cost for the week (Monday-aligned). Save again to update.</p>
      </div>

      <form onSubmit={save} className="bg-white border border-zinc-200 p-5 mb-6 grid md:grid-cols-[200px_1fr_auto] gap-3 items-end" data-testid="food-cost-form">
        <div>
          <Label className="label-uc">Week starting</Label>
          <Input type="date" value={weekStart} onChange={(e) => onWeekChange(e.target.value)}
            className="rounded-none border-zinc-300 mt-1.5 font-mono-data" required data-testid="food-cost-week-input" />
          <div className="text-[10px] text-zinc-500 mt-1">Auto-snaps to Monday</div>
        </div>
        <div>
          <Label className="label-uc">Amount £</Label>
          <Input type="number" step="0.01" min="0" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="rounded-none border-zinc-300 mt-1.5 font-mono-data text-lg" required data-testid="food-cost-amount-input" />
        </div>
        <Button type="submit" disabled={saving} className="rounded-none bg-zinc-950 hover:bg-zinc-800 h-10" data-testid="food-cost-submit-button">
          <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : (existingId ? "Update" : "Save")}
        </Button>
      </form>

      <div className="bg-white border border-zinc-200">
        <table className="w-full text-sm" data-testid="food-cost-table">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="px-4 py-2 label-uc">Week starting</th>
              <th className="px-4 py-2 label-uc text-right">Amount</th>
              <th className="px-4 py-2 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 && (
              <tr><td colSpan={3} className="px-4 py-12 text-center text-zinc-400">No weekly food costs logged yet.</td></tr>
            )}
            {history.map((r) => (
              <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`food-cost-row-${r.id}`}>
                <td className="px-4 py-2 font-mono-data">{r.week_start}</td>
                <td className="px-4 py-2 text-right font-mono-data font-semibold">{gbp(r.amount)}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => remove(r.id)} className="text-zinc-400 hover:text-[#DF1A22]" data-testid={`food-cost-delete-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
